import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Church,
  Users,
  Calendar,
  DollarSign,
  MessageSquare,
  Heart,
  BookOpen,
  BarChart3,
  Shield,
  Smartphone,
  CheckCircle,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
} from 'lucide-react'

interface LandingModernProps {
  onGetStarted: () => void
}

export default function LandingModern({ onGetStarted }: LandingModernProps) {
  console.log('🏠 [DEBUG] LandingModern component rendered - FaithYoke LANDING PAGE')

  useEffect(() => {
    document.title = 'FaithYoke - Church Management Software for African Churches'
  }, [])

  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const features = [
    {
      icon: Users,
      title: 'Member Management',
      description: 'Comprehensive member database with profiles, family grouping, and engagement tracking.'
    },
    {
      icon: Calendar,
      title: 'Events & Services',
      description: 'Schedule services, events, and track attendance with our intuitive calendar.'
    },
    {
      icon: DollarSign,
      title: 'Financial Management',
      description: 'Track contributions, expenses, budgets, and generate comprehensive reports.'
    },
    {
      icon: MessageSquare,
      title: 'Communications',
      description: 'Send SMS, push notifications, and emails to members instantly.'
    },
    {
      icon: Heart,
      title: 'Ministry Tools',
      description: 'Manage prayer requests, testimonies, counseling, and evangelism programs.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Apps',
      description: 'Native mobile apps for iOS and Android keep your members connected.'
    },
  ]

  const pricingPlans = [
    {
      name: 'Starter',
      price: 'GHS 199',
      period: '/month',
      annualPrice: 'GHS 1,910/year',
      description: 'Perfect for small to medium churches',
      features: [
        '500 members',
        '5 admin accounts',
        '10GB storage',
        '300 SMS/month',
        'Basic finance tracking',
      ],
    },
    {
      name: 'Growth',
      price: 'GHS 499',
      period: '/month',
      annualPrice: 'GHS 4,790/year',
      description: 'Scale your ministry',
      features: [
        '2,000 members',
        'Unlimited admins',
        '50GB storage',
        '1,000 SMS/month',
        'Complete finance (GL, budgets)',
      ],
      popular: true,
    },
    {
      name: 'Leadership',
      price: 'GHS 1,499',
      period: '/month',
      annualPrice: 'GHS 14,390/year',
      description: 'Enterprise solutions',
      features: [
        'Unlimited members',
        'HR & Payroll',
        '500GB storage',
        '3,000 SMS/month',
        'White-label mobile app',
      ],
    },
  ]

  console.log('🏠 [DEBUG] Pricing plans:', pricingPlans.map(p => ({ name: p.name, price: p.price })))

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-white">
                <span>Faith</span><span className="text-orange-500">Y</span><span>oke</span>
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-white/90 hover:text-white transition-colors text-sm font-medium uppercase tracking-wider">
                Features
              </a>
              <a href="#pricing" className="text-white/90 hover:text-white transition-colors text-sm font-medium uppercase tracking-wider">
                Pricing
              </a>
              <a href="#about" className="text-white/90 hover:text-white transition-colors text-sm font-medium uppercase tracking-wider">
                About
              </a>
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-white/90 hover:text-white hover:bg-white/10"
              >
                Sign In
              </Button>
              <Button
                onClick={onGetStarted}
                className="bg-white text-sky-500 hover:bg-white/90"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-white/10">
            <div className="px-6 py-6 space-y-4">
              <a href="#features" className="block text-white/90 hover:text-white py-2">Features</a>
              <a href="#pricing" className="block text-white/90 hover:text-white py-2">Pricing</a>
              <a href="#about" className="block text-white/90 hover:text-white py-2">About</a>
              <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
                Sign In
              </Button>
              <Button onClick={onGetStarted} className="w-full bg-white text-sky-500">
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Split Screen */}
      <section className="relative h-screen overflow-hidden">
        {/* Split Container */}
        <div className="absolute inset-0 grid lg:grid-cols-2">
          {/* Left Side - Gradient */}
          <div className="relative bg-gradient-to-br from-sky-500 via-sky-600 to-cyan-500 overflow-hidden">
            {/* Subtle Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full bg-white blur-3xl"></div>
            </div>
          </div>

          {/* Right Side - Church Image */}
          <div className="relative bg-slate-900">
            <img
              src="/images/church-interior.jpg"
              alt="Church Interior"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-sky-900/40"></div>
          </div>
        </div>

        {/* Centered Large Typography Spanning Both Sides */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="max-w-7xl mx-auto px-8 lg:px-16 w-full">
            <h1 className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold text-white leading-none tracking-tight">
              <span className="block">Church</span>
              <span className="block">Management</span>
              <span className="block text-white/90">Platform</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl lg:text-2xl text-white/90 max-w-2xl font-light">
              Complete solution for managing and growing your congregation.
            </p>

            {/* CTA Buttons */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 pointer-events-auto">
              <Button
                size="lg"
                onClick={onGetStarted}
                className="bg-white text-sky-500 hover:bg-white/90 text-lg px-8 h-14"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-2 border-white text-white hover:bg-white hover:text-sky-500 text-lg px-8 h-14"
              >
                Sign In
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 flex flex-wrap items-center gap-6 text-white/70 text-sm pointer-events-auto">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>30-Day Free Trial</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>No Credit Card Required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">Everything Your Church Needs</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="p-6 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-sky-500 dark:hover:border-sky-500 transition-colors">
                <div className="mb-4 w-12 h-12 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 lg:px-12 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">Choose Your Plan</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 mt-4">✨ NEW PRICING - 30-day free trial • No credit card required ✨</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`p-8 rounded-lg bg-white dark:bg-slate-800 ${
                  plan.popular
                    ? 'border-2 border-sky-500 shadow-xl'
                    : 'border border-slate-200 dark:border-slate-700'
                }`}
              >
                {plan.popular && (
                  <div className="mb-4">
                    <span className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">{plan.description}</p>

                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    {plan.period && (
                      <span className="text-slate-600 dark:text-slate-400 ml-1">{plan.period}</span>
                    )}
                  </div>
                  {plan.annualPrice && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      or {plan.annualPrice}/year
                    </p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                    No setup fees. Save 20% with annual billing.
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full ${plan.popular ? 'bg-gradient-to-r from-sky-500 to-cyan-500' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={onGetStarted}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join churches across Africa using FaithYoke.
          </p>
          <Button
            size="lg"
            className="bg-white text-sky-500 hover:bg-white/90 text-lg px-8 h-14"
            onClick={onGetStarted}
          >
            Start Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500">
                  <Church className="h-5 w-5" />
                </div>
                <span className="font-bold">
                  <span>Faith</span><span className="text-orange-500">Y</span><span>oke</span>
                </span>
              </div>
              <p className="text-sm text-slate-400">
                Take my yoke upon you and learn from me…
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm">Product</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm">Company</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#about" className="hover:text-white">About</a></li>
                <li><a href="mailto:support@faithyoke.com" className="hover:text-white">Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-sm">Contact</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>support@faithyoke.com</li>
                <li>Accra, Ghana</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} FaithYoke. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
